/* eslint-disable jsx-a11y/tabindex-no-positive */
import * as Yup from 'yup'
import FormikTextField from '@ui-components-3/compat/lib/components/FormikTextField'
import PasswordInput from '@ui-components-3/ui/lib/components/PasswordInput'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import TestIds from 'util/TestIds'
import useDocumentTitle from 'hooks/useDocumentTitle'
import EntryFormCard from '../EntryFormCard'
import { PwValues } from 'pages/EntryPage/EntryPage'

const PASSWORD_VISIBILITY_TOGGLER_PROPS = {
  'data-testid': TestIds.login.button.togglePasswordVisibility,
  tabIndex: 2,
}

export type LoginPwSceneProps = {
  onSubmit: (values: PwValues, form: FormikHelpers<PwValues>) => void
  onForgotPassword: () => void
  onBack: () => void
}

const LoginPasswordScene = (props: LoginPwSceneProps): JSX.Element => {
  const { t } = useTranslation()
  useDocumentTitle(t('login.password.pageTitle'))

  const PasswordSchema = Yup.object().shape({
    password: Yup.string()
      .label(t('login.password.passwordLabel'))
      .matches(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{10,}$/g,
        t('login.password.validationMessage'),
      )
      .required(),
  })

  return (
    <EntryFormCard prompt={t('login.password.prompt')} data-testid={TestIds.login.view.password}>
      <p className="typography-body self-start pb-4 text-start text-neutral-600">{t('common.requiredFields')}</p>
      <Formik initialValues={{ password: '' }} validationSchema={PasswordSchema} onSubmit={props.onSubmit}>
        {(renderProps) => {
          const { dirty, isValid, handleSubmit } = renderProps
          return (
            <form onSubmit={handleSubmit} className="flex flex-col items-center">
              <FormikTextField
                fullWidth
                data-testid={TestIds.login.input.password}
                InputComponent={PasswordInput}
                VisibilityTogglerProps={PASSWORD_VISIBILITY_TOGGLER_PROPS}
                schema={PasswordSchema}
                label={t('login.password.passwordLabel')}
                name="password"
                autoComplete="current-password"
                className="order-2"
                tabIndex={1}
              />

              <button
                type="button"
                className="typography-body text-primary order-1 -mb-2 cursor-pointer self-end font-semibold hover:underline"
                data-testid={TestIds.login.button.forgot}
                onClick={props.onForgotPassword}
                tabIndex={3}
              >
                {t('login.password.forgotPassword')}
              </button>

              <div className="gap-s order-3 mt-9 flex w-full items-center justify-between">
                <button
                  className="btn btn-neutral-outlined grow"
                  data-testid={TestIds.login.button.cancel}
                  onClick={props.onBack}
                  type="button"
                  tabIndex={4}
                >
                  {t('common.back')}
                </button>
                <button
                  className="btn btn-primary grow"
                  data-testid={TestIds.login.button.login}
                  disabled={!dirty || !isValid}
                  type="submit"
                  tabIndex={5}
                >
                  {t('common.continue')}
                </button>
              </div>
            </form>
          )
        }}
      </Formik>
    </EntryFormCard>
  )
}

export default LoginPasswordScene
